import {
    fromMonetaryToInt,
    monetaryValuesEqual,
    roundToCents,
} from "@common/helpers/monetary";
import {
    allocateProRataByOpenAmount,
    OpenWeightedLine,
} from "@common/helpers/pro-rata-allocation";
import { LineItemAllocation } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { MatchableItem, MatchableLineItem, MatchItemType } from "../types";

export interface AllocationTotals {
    readonly transactionItemsTotal: number;
    readonly allocatedTotal: number;
    readonly amountToBeAllocated: number;
    readonly remainingAmount: number;
}

export function documentMatchItemType(
    documentItems: MatchableItem[],
): MatchItemType | null {
    if (documentItems.length === 0) {
        return null;
    }
    if (documentItems.every((item) => item.type === MatchItemType.INVOICE)) {
        return MatchItemType.INVOICE;
    }
    if (documentItems.every((item) => item.type === MatchItemType.BILL)) {
        return MatchItemType.BILL;
    }
    return null;
}

export function getAllLineItems(
    selectedItems: MatchableItem[],
): Array<{ parentItem: MatchableItem; lineItem: MatchableLineItem }> {
    return selectedItems.flatMap((item) =>
        (item.lineItems ?? []).map((li) => ({
            parentItem: item,
            lineItem: li,
        })),
    );
}

export function buildInitialPaymentAmounts(
    items: MatchableItem[],
    transactionAmount: number,
    currentTxMatchAmounts?: ReadonlyMap<string, number>,
): Record<string, number> {
    let remaining = Math.abs(transactionAmount);
    const preserved: Array<[string, number]> = [];
    const unallocated: OpenWeightedLine[] = [];

    // Keep amounts this transaction already applied. Split whatever is left
    // across the other lines in proportion to their open amounts, never above
    // what each line still has open.
    for (const { lineItem } of getAllLineItems(items)) {
        const existing = currentTxMatchAmounts?.get(lineItem.id) ?? 0;
        if (existing > 0) {
            const cappedExisting = roundToCents(Math.min(remaining, existing));
            preserved.push([lineItem.id, cappedExisting]);
            remaining = roundToCents(remaining - cappedExisting);
            continue;
        }
        unallocated.push({
            id: lineItem.id,
            openAmount: lineItem.openAmount,
        });
    }

    return Object.fromEntries([
        ...preserved,
        ...allocateProRataByOpenAmount(unallocated, remaining),
    ]);
}

export function toLineItemAllocations(
    items: MatchableItem[],
    paymentAmounts: Record<string, number>,
): LineItemAllocation[] {
    return getAllLineItems(items)
        .map(({ lineItem }) => ({
            lineItemId: lineItem.id,
            paymentAmount: paymentAmounts[lineItem.id] ?? 0,
        }))
        .filter((allocation) => allocation.paymentAmount > 0);
}

/**
 * The allocations "Edit allocation" would prefill, or `null` when a selected
 * document has no line items to allocate against.
 */
export function buildDefaultLineItemAllocations({
    items,
    transactionAmount,
    currentTxMatchAmounts,
}: {
    items: MatchableItem[];
    transactionAmount: number;
    currentTxMatchAmounts?: ReadonlyMap<string, number>;
}): LineItemAllocation[] | null {
    const documents = items.filter(
        (item) => item.type !== MatchItemType.TRANSACTION,
    );
    const isEveryDocumentAllocatable =
        documents.length > 0 &&
        documents.every((document) => (document.lineItems?.length ?? 0) > 0);
    if (!isEveryDocumentAllocatable) {
        return null;
    }
    const allocations = toLineItemAllocations(
        items,
        buildInitialPaymentAmounts(
            items,
            transactionAmount,
            currentTxMatchAmounts,
        ),
    );
    return allocations.length > 0 ? allocations : null;
}

export function clampPaymentAmount(value: number, maxOpen: number): number {
    return roundToCents(Math.max(0, Math.min(value, maxOpen)));
}

export function lineItemToMatchableItem(
    li: MatchableLineItem,
    parent: MatchableItem,
): MatchableItem {
    return {
        id: li.id,
        date: parent.date,
        type: MatchItemType.LINE_ITEM,
        counterpartyName: null,
        description: li.description,
        accountIds: li.accountId ? [li.accountId] : [],
        accrualAccountIds: li.accountId ? [li.accountId] : [],
        cashAccountIds: li.cashAccountIds,
        missingIncomeFallbacks: li.missingIncomeFallbacks,
        classIds: li.classIds,
        amount: li.amount,
        openAmount: li.openAmount,
        entityId: parent.entityId,
        entity: parent.entity,
        invoiceSource: parent.invoiceSource,
        lineItems: parent.lineItems,
    };
}

export function buildNestedItems(items: MatchableItem[]): MatchableItem[] {
    return items.map((item) => {
        const lineItems = item.lineItems ?? [];
        if (lineItems.length === 0) {
            return item;
        }
        return {
            ...item,
            children: lineItems.map((li) => lineItemToMatchableItem(li, item)),
        };
    });
}

// Whether the edit-allocation drawer's Save action can be submitted.
// Accrual entities must land on an exact allocation — any leftover has to be
// booked to an account via "Resolve difference". Cash-basis entities have no
// such account to book to (the backend rejects a `remaining` payload without an
// accrual ledger), so the leftover is left for the backend to park on an
// uncategorized split automatically; Save only needs to guard against
// over-allocating past the transaction amount. The `> 0` floor keeps Save
// disabled when every match was removed (or nothing was ever allocated) instead
// of submitting an empty body the backend would just reject. Unmatching a
// saved match is confirmed from the match command bar.
export function getIsAllocationSaveEnabled({
    hasAccrualLedger,
    allocatedTotal,
    amountToBeAllocated,
}: {
    hasAccrualLedger: boolean;
    allocatedTotal: number;
    amountToBeAllocated: number;
}): boolean {
    if (hasAccrualLedger) {
        return monetaryValuesEqual(allocatedTotal, amountToBeAllocated);
    }
    return (
        allocatedTotal > 0 &&
        fromMonetaryToInt(allocatedTotal) <=
            fromMonetaryToInt(amountToBeAllocated)
    );
}
